import React from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { ArrowLeft, ClockCounterClockwise, ShieldStar, Eye, UserPlus } from '@phosphor-icons/react';

const ACTION_LABELS = {
    view: 'Viewed household',
    join_as_coordinator: 'Joined as coordinator',
};

const ACTION_CLS = {
    view: 'bg-slate-100 text-slate-700',
    join_as_coordinator: 'bg-emerald-50 text-emerald-800',
};

const ACTION_ICON = {
    view: Eye,
    join_as_coordinator: UserPlus,
};

export default function AdminAccessLog() {
    const { data: items = [], isLoading } = useQuery({
        queryKey: ['admin-access-log'],
        queryFn: async () => (await api.get('/admin/access-log', { params: { limit: 500 } })).data,
    });

    return (
        <div className="px-6 md:px-10 py-8 max-w-5xl" data-testid="admin-access-log-root">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-2">
                <ShieldStar size={12} weight="duotone" /> Tanow staff
            </div>
            <header className="mb-6 flex items-end justify-between">
                <div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 flex items-center gap-3">
                        <ClockCounterClockwise size={30} className="text-primary" /> Admin access log
                    </h1>
                    <p className="text-slate-600 mt-2">Every household an admin views or joins. Ordered newest first.</p>
                </div>
                <Link to="/admin/households" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1">
                    <ArrowLeft size={14} /> All households
                </Link>
            </header>

            <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                <div className="grid grid-cols-[170px_180px_1fr_1fr] px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div>When</div><div>Action</div><div>Admin</div><div>Household</div>
                </div>
                {isLoading ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="px-5 py-8 text-sm text-slate-500 text-center">No admin oversight activity yet.</div>
                ) : (
                    <div className="divide-y divide-slate-100">
                        {items.map(a => {
                            const Icon = ACTION_ICON[a.action] || Eye;
                            return (
                                <div key={a.access_id} data-testid="admin-access-log-row" className="grid grid-cols-[170px_180px_1fr_1fr] px-5 py-3 items-center text-sm">
                                    <div className="text-slate-500">{new Date(a.timestamp).toLocaleString()}</div>
                                    <div>
                                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded inline-flex items-center gap-1 ${ACTION_CLS[a.action] || 'bg-slate-100 text-slate-700'}`}>
                                            <Icon size={11} weight="bold" />
                                            {ACTION_LABELS[a.action] || a.action}
                                        </span>
                                    </div>
                                    <div className="text-slate-800 truncate">
                                        <div className="text-slate-900">{a.admin_name || a.admin_email}</div>
                                        <div className="text-[11px] text-slate-500 truncate">{a.admin_email}</div>
                                    </div>
                                    <div className="text-slate-800 truncate">
                                        <Link to={`/admin/households/${a.facility_id}`} className="text-slate-900 hover:underline">
                                            {a.facility_name || a.facility_id}
                                        </Link>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
